import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  BbWinRecord,
  bbApproveWin,
  bbGetWinRecords,
  bbRejectWin,
} from '../../../services/blindBoxService';
import dayjs from 'dayjs';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import { BB_REWARD_CATEGORY_FILTER_OPTIONS } from '../shared/rewardCategories';

export function SectionWinRecords({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  type SourceFilter = '' | 'BOX' | 'WHEEL' | 'LOTTERY';
  type StatusFilter = '' | 'PENDING' | 'UNDER_REVIEW' | 'DISTRIBUTED' | 'REJECTED';
  const [source, setSource] = useState<SourceFilter>('WHEEL');
  const [status, setStatus] = useState<StatusFilter>('PENDING');
  const [rewardCategory, setRewardCategory] = useState<string>('');
  const [walletAddress, setWalletAddress] = useState<string>('');
  const [rows, setRows] = useState<BbWinRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [noteDlg, setNoteDlg] = useState<{ id: string } | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const load = useCallback(() => {
    bbGetWinRecords({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      source: source || undefined,
      status: status || undefined,
      rewardCategory: rewardCategory || undefined,
      walletAddress: walletAddress || undefined,
    }).then((r) => {
      setRows(r.entries || []);
      setTotal(r.total || 0);
    });
  }, [source, status, rewardCategory, walletAddress, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [source, status, rewardCategory]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);


  const approve = async (id: string) => {
    try {
      await bbApproveWin(id);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const reject = async () => {
    if (!noteDlg) return;
    try {
      await bbRejectWin(noteDlg.id, rejectNote);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setNoteDlg(null);
      setRejectNote('');
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <ToggleButtonGroup
          exclusive
          value={source}
          onChange={(_, v) => {
            if (v === null) return;
            setSource(v as SourceFilter);
          }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': { px: 2, textTransform: 'none' },
          }}
        >
          <ToggleButton value="WHEEL">{t('module.blindBox.winTab_wheel')}</ToggleButton>
          <ToggleButton value="BOX">{t('module.blindBox.winTab_box')}</ToggleButton>
          {/*<ToggleButton value="LOTTERY">{t('module.blindBox.winTab_lottery')}</ToggleButton>*/}
        </ToggleButtonGroup>
        <TextField
          select
          size="small"
          label="status"
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">(all)</MenuItem>
          <MenuItem value="PENDING">PENDING</MenuItem>
          <MenuItem value="UNDER_REVIEW">UNDER_REVIEW</MenuItem>
          <MenuItem value="DISTRIBUTED">DISTRIBUTED</MenuItem>
          <MenuItem value="REJECTED">REJECTED</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="rewardCategory"
          value={rewardCategory}
          onChange={(e) => setRewardCategory(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">(all)</MenuItem>
          {BB_REWARD_CATEGORY_FILTER_OPTIONS.map((x) => (
            <MenuItem key={x} value={x}>
              {x}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="walletAddress"
          value={walletAddress}
          onChange={(e) => setWalletAddress(e.target.value as string)}
          sx={{ minWidth: 180 }}
        >
        </TextField>

        <Button onClick={load}>{t('module.blindBox.refresh')}</Button>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>source</TableCell>
              <TableCell>address</TableCell>
              <TableCell>rewardCategory</TableCell>
              <TableCell>reward</TableCell>
              <TableCell>amount</TableCell>
              <TableCell>status</TableCell>
              <TableCell>time</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((w) => (
              <TableRow key={w.id}>
                <TableCell>{w.source}</TableCell>
                <TableCell>{w.walletAddress}</TableCell>
                <TableCell>{w.rewardCategory}</TableCell>
                <TableCell>{w.rewardName}</TableCell>
                <TableCell>{w.amount}</TableCell>
                <TableCell>{w.status}</TableCell>
                <TableCell>{dayjs(w.createdAt).format('YYYY-MM-DD HH:mm')}</TableCell>
                <TableCell align="right">
                  <PermissionButton resource="bb-win-records" action="update">
                    <Button
                      size="small"
                      onClick={() => approve(w.id)}
                      // 关键：非 PENDING 就禁用
                      disabled={w.status !== 'PENDING' && w.status !== 'UNDER_REVIEW'}
                    >
                      {t('module.blindBox.approve')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setNoteDlg({ id: w.id })}
                      // 关键：非 PENDING 就禁用
                      disabled={w.status !== 'PENDING' && w.status !== 'UNDER_REVIEW'}
                    >
                      {t('module.blindBox.reject')}
                    </Button>
                  </PermissionButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          const next = parseInt(e.target.value, 10);
          setRowsPerPage(Number.isFinite(next) && next > 0 ? next : 50);
          setPage(0);
        }}
        rowsPerPageOptions={[20, 50, 100]}
      />

      <Dialog open={!!noteDlg} onClose={() => setNoteDlg(null)}>
        <DialogTitle>{t('module.blindBox.reject')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDlg(null)}>{t('actions.cancel')}</Button>
          <PermissionButton resource="bb-win-records" action="update">
            <Button variant="contained" color="error" onClick={reject}>
              {t('module.blindBox.reject')}
            </Button>
          </PermissionButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
